import React from 'react';
import { Box, Typography } from '@mui/material';
import { CREDIT_LIMITS } from '../utils/constants.js';

/**
 * CreditChip component with progress bar styling
 * @param {number} credits - Current credits
 * @param {number} maxCredits - Maximum credits for this type (optimal target)
 * @param {string} type - Type of credits ("semester", "period", or "total")
 * @param {Function} getColorFn - Function to determine chip color (used for determining progress color)
 * @param {string} variant - Material-UI chip variant (not used in progress version)
 * @param {string} size - Size ("small", "medium", "large")
 */
const CreditChip = ({ 
  credits, 
  maxCredits, 
  type, 
  getColorFn, 
  variant = "filled", 
  size = "medium" 
}) => {
  // Get optimal target based on type
  const getOptimalTarget = () => {
    if (type === 'semester') {
      return CREDIT_LIMITS.SEMESTER_OPTIMAL; // 30
    } else if (type === 'period') {
      return CREDIT_LIMITS.PERIOD_OPTIMAL; // 15
    } else {
      return maxCredits; // For total/other types
    }
  };

  const optimalTarget = getOptimalTarget();
  
  // Calculate primary progress percentage (0-100%)
  const primaryProgress = Math.min(credits / optimalTarget, 1) * 100;
  
  // Calculate secondary progress (excess beyond optimal) - disabled for total type
  const excessCredits = type === 'total' ? 0 : Math.max(0, credits - optimalTarget);
  const getSecondaryRange = () => {
    if (type === 'semester') {
      return CREDIT_LIMITS.SEMESTER_MAX - CREDIT_LIMITS.SEMESTER_OPTIMAL; // 45 - 30 = 15
    } else if (type === 'period') {
      return 10; // Reasonable excess range for periods
    } else {
      return maxCredits - optimalTarget;
    }
  };
  const secondaryRange = getSecondaryRange();
  const secondaryProgress = excessCredits > 0 ? Math.min(excessCredits / secondaryRange, 1) * 100 : 0;

  // Determine progress color using existing color function
  const getProgressColor = () => {
    if (credits === 0) return '#616161'; // Darker gray for empty (visible on dark background)
    
    const colorResult = getColorFn(credits);
    if (colorResult === 'success') return '#4caf50'; // Green for optimal range
    return '#f44336'; // Red for outside optimal range
  };

  // Determine secondary progress color (for excess credits)
  const getSecondaryColor = () => {
    if (excessCredits === 0) return 'transparent';
    
    // Check if we're still within tolerance
    const toleranceLimit = type === 'semester' 
      ? CREDIT_LIMITS.SEMESTER_OPTIMAL + CREDIT_LIMITS.SEMESTER_OPTIMAL_RANGE // 30 + 5 = 35
      : CREDIT_LIMITS.PERIOD_OPTIMAL + CREDIT_LIMITS.PERIOD_OPTIMAL_RANGE; // 15 + 3 = 18
    
    if (credits <= toleranceLimit) {
      return '#2e7d32'; // Darker green - still within tolerance
    } else {
      return '#d32f2f'; // Darker red - exceeding tolerance
    }
  };

  // Size configurations
  const sizeConfig = {
    small: { height: 20, fontSize: '0.75rem', px: 1.5, borderRadius: 10 },
    medium: { height: 24, fontSize: '0.875rem', px: 2, borderRadius: 12 },
    large: { height: 32, fontSize: '1rem', px: 2.5, borderRadius: 16 }
  };

  const config = sizeConfig[size] || sizeConfig.medium;
  const label = `${credits}/${maxCredits} credits`;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        height: config.height,
        minWidth: 100,
        backgroundColor: '#424242',
        border: '1px solid #616161',
        borderRadius: config.borderRadius,
        overflow: 'hidden',
        px: config.px
      }}
    >
      {/* Primary Progress Bar - fills as you approach optimal */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${primaryProgress}%`,
          backgroundColor: getProgressColor(),
          transition: 'width 0.3s ease-in-out',
          zIndex: 1
        }}
      />
      
      {/* Secondary Progress Bar - appears when exceeding optimal */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${secondaryProgress}%`,
          backgroundColor: getSecondaryColor(),
          transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, background-color 0.3s ease-in-out',
          zIndex: 2,
          opacity: secondaryProgress > 0 ? 0.9 : 0
        }}
      />
      
      {/* Text Label with dynamic color */}
      <Typography
        variant="body2"
        sx={{
          position: 'relative',
          zIndex: 3,
          fontSize: config.fontSize,
          fontWeight: 600,
          color: primaryProgress > 30 ? 'white' : '#e0e0e0',
          textShadow: primaryProgress > 30 ? '0 1px 2px rgba(0,0,0,0.5)' : '0 1px 2px rgba(0,0,0,0.5)',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default CreditChip; 